import {
  Box,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import useFetch from "hooks/useFetch";
import useCautionStore from "store/cautionStore";
import { IListedDomain } from "interfaces/domains";
import { styled } from "@mui/material/styles";
import PermIdentityIcon from "@mui/icons-material/PermIdentity";
import DomainIcon from "@mui/icons-material/Domain";
import moment from "moment";
import { getToken } from "utils/getToken";
import { toast } from "react-toastify";
import { BootstrapTooltip } from "components/PWATabs/mainTab/CommentsTable";
import DeleteIcon from "@mui/icons-material/Delete";

interface IListedDomainsTabProps {
  listedDomains: IListedDomain[];
  page: number;
  totalListedDomains: number;
  handleChangePage: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => void;
  getAllListedDomains: () => Promise<void>;
}

const TypographyStyled = styled(Typography)(() => {
  return {
    color: "#6D6777",
    fontWeight: 500,
    fontFamily: "var(--cygreRegular), sans-serif",
  };
});

export const CustomSwitch = styled(Switch)(({ theme }) => ({
  padding: 8,
  "& .MuiSwitch-track": {
    borderRadius: 22 / 2,
    "&::before, &::after": {
      content: '""',
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      width: 16,
      height: 16,
    },
    "&::before": {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="%23fff" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/></svg>')`,
      left: 12,
    },
    "&::after": {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="%23fff" d="M19,13H5V11H19V13Z" /></svg>')`,
      right: 12,
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "none",
    width: 16,
    height: 16,
    margin: 2,
  },
}));

const AdminListedDomainsTab = ({
  listedDomains,
  page,
  totalListedDomains,
  handleChangePage,
  getAllListedDomains,
}: IListedDomainsTabProps) => {
  const token = getToken();
  const { t } = useTranslation();
  const { handleDomainToDeleteChange, openDomainDeleteModal } = useCautionStore();

  const { triggerFetch: updateListedDomainVisibility } = useFetch({
    url: `admin/domains/visibility`,
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const handleSwitchChange = async (event: React.ChangeEvent<HTMLInputElement>, id: string) => {
    try {
      const updatedListedDomain = await updateListedDomainVisibility({ id, isVisible: event.target.checked });
      if (updatedListedDomain?.success) {
        await getAllListedDomains();
        toast.success(t("successDomainVisibilityChange"));
      }
    } catch (error) {
      toast.error(t("unsuccessfulDomainVisibilityChange"));
    }
  };

  const handleDeleteDomainBtnClick = (domainShortInfo: { id: string; name: string }) => {
    handleDomainToDeleteChange(domainShortInfo);
    openDomainDeleteModal();
  };

  return (
    <Box sx={{ width: "100%", overflowY: "auto" }}>
      <Table aria-label="simple table">
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: "#F6F7FB",
            }}
          >
            <TableCell
              sx={{
                paddingLeft: "36px",
                borderTopLeftRadius: "8px",
                borderBottomLeftRadius: "8px",
              }}
            >
              <TypographyStyled>{t("domain")}</TypographyStyled>
            </TableCell>
            <TableCell>
              <TypographyStyled>{t("status")}</TypographyStyled>
            </TableCell>
            <TableCell>
              <TypographyStyled>{t("user")}</TypographyStyled>
            </TableCell>
            <TableCell>
              <TypographyStyled sx={{ whiteSpace: "nowrap", overflow: "hidden" }}>
                {t("expirationdate")}
              </TypographyStyled>
            </TableCell>
            <TableCell sx={{ borderTopRightRadius: "8px", borderBottomRightRadius: "8px" }}>
              <TypographyStyled sx={{ textAlign: "center", whiteSpace: "nowrap", overflow: "hidden" }}>
                {t("isVisibleToUser")}
              </TypographyStyled>
            </TableCell>
            <TableCell>
              <TypographyStyled
                sx={{
                  textAlign: "center",
                }}
              >
                {t("actions")}
              </TypographyStyled>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {listedDomains?.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6}>
                <Typography variant="subtitle2">{t("noitems")}</Typography>
              </TableCell>
            </TableRow>
          ) : (
            listedDomains.map(({ _id, name, status, userId, expiredAt, isVisible }) => {
              const formattedDate = expiredAt ? moment(expiredAt).format("DD.MM.YYYY HH:mm:ss") : "—";
              const listedDomainShortInfo = { id: _id, name };

              return (
                <TableRow key={_id}>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "12px", paddingLeft: "18px" }}>
                      <DomainIcon
                        sx={{
                          color: "#6D6777",
                          width: "26px",
                          height: "26px",
                        }}
                      />

                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: "400",
                            fontFamily: "var(--cygreRegular), sans-serif",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                          }}
                        >
                          {name}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "10px",
                            fontWeight: "400",
                            fontFamily: "var(--cygreRegular), sans-serif",
                            color: "#6D6777",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                          }}
                        >
                          {`id: ${_id}`}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none" }}>
                    <Typography
                      sx={{
                        color: status === "free" ? "#16B364" : "#F4701B",
                        fontSize: "14px",
                        fontWeight: "400",
                        fontFamily: "var(--cygreRegular), sans-serif",
                      }}
                    >
                      {status}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none" }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      {userId && (
                        <PermIdentityIcon
                          sx={{
                            width: "18px",
                            height: "18px",
                          }}
                        />
                      )}
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: "400",
                          fontFamily: "var(--cygreRegular), sans-serif",
                        }}
                      >
                        {userId ? userId : "—"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none" }}>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: "400",
                        fontFamily: "var(--cygreRegular), sans-serif",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                      }}
                    >
                      {formattedDate}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none" }}>
                    <Stack direction="row" spacing={1} sx={{ justifyContent: "center", alignItems: "center" }}>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: "400",
                          fontFamily: "var(--cygreRegular), sans-serif",
                        }}
                      >
                        Off
                      </Typography>
                      <CustomSwitch
                        checked={isVisible}
                        onChange={(event) => handleSwitchChange(event, _id)}
                        slotProps={{ input: { "aria-label": "ant design" } }}
                      />
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: "400",
                          fontFamily: "var(--cygreRegular), sans-serif",
                        }}
                      >
                        On
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none" }}>
                    <BootstrapTooltip title={t("delete")} placement="top">
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          cursor: "pointer",
                        }}
                        onClick={() => handleDeleteDomainBtnClick(listedDomainShortInfo)}
                      >
                        <DeleteIcon sx={{ color: "#6D6777" }} />
                      </Box>
                    </BootstrapTooltip>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[]}
        component="div"
        count={totalListedDomains}
        rowsPerPage={20}
        page={page}
        labelRowsPerPage={null}
        labelDisplayedRows={() => ""}
        onPageChange={handleChangePage}
        sx={{
          "& .MuiTablePagination-actions": { margin: "0px !important" },
          "& .MuiTablePagination-spacer": {
            display: "none",
          },
          "& .MuiTablePagination-toolbar": {
            padding: 0,
            justifyContent: "flex-start",
          },
        }}
      />
    </Box>
  );
};

export default AdminListedDomainsTab;
